import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { string, object, type InferType, ref } from "yup";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  PasswordInput,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@envirohub/ui";
import { AlertCircle, ArrowLeft, CheckCircle2, X } from "lucide-react";
import { clearPasswordInputs } from "@envirohub/utils";
import { useForgotPassword, useResetPassword } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<"request" | "verify" | "reset" | "success">("request");
  const [contact, setContact] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [requestError, setRequestError] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  const resolveAuthError = useAuthError();
  const forgotPassword = useForgotPassword();
  const resetPassword = useResetPassword();

  const requestSchema = useMemo(
    () =>
      object({
        email: string().required(t("validation.emailRequired")).email(t("validation.invalidEmail")),
      }),
    [t],
  );
  type RequestData = InferType<typeof requestSchema>;

  const otpSchema = useMemo(
    () =>
      object({
        otp: string().required(t("validation.otpMin")).min(4, t("validation.otpMin")),
      }),
    [t],
  );
  type OtpData = InferType<typeof otpSchema>;

  const resetSchema = useMemo(
    () =>
      object({
        password: string()
          .required("validation.passwordRequired")
          .min(8, "validation.passwordRequirements")
          .matches(/[A-Z]/, "validation.passwordRequirements")
          .matches(/[a-z]/, "validation.passwordRequirements")
          .matches(/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, "validation.passwordRequirements"),
        confirmPassword: string()
          .oneOf([ref("password")], "validation.passwordsMatch")
          .required("validation.passwordsMatch"),
      }),
    [],
  );
  type ResetData = InferType<typeof resetSchema>;

  const passwordRequirementLabels = {
    length: t("validation.passwordLength"),
    upper: t("validation.passwordUpper"),
    lower: t("validation.passwordLower"),
    numberOrSpecial: t("validation.passwordNumberOrSpecial"),
  };

  // Forms
  const requestForm = useForm<RequestData>({
    resolver: yupResolver(requestSchema),
  });
  const otpForm = useForm<OtpData>({ resolver: yupResolver(otpSchema) });
  const resetForm = useForm<ResetData>({ resolver: yupResolver(resetSchema) });

  const onRequestSubmit = (data: RequestData) => {
    setRequestError(null);
    forgotPassword.mutate(
      { email: data.email },
      {
        onSuccess: () => {
          setContact(data.email);
          setStep("verify");
        },
        onError: (error) => {
          // Endpoint never enumerates accounts; only infra errors land here.
          setRequestError(resolveAuthError(error).message);
        },
      },
    );
  };

  const onOtpSubmit = (data: OtpData) => {
    setOtpError(null);
    setOtpCode(data.otp);
    setStep("reset");
  };

  const onResetSubmit = (data: ResetData) => {
    setResetError(null);
    resetPassword.mutate(
      { email: contact, code: otpCode, newPassword: data.password },
      {
        onSuccess: () => {
          clearPasswordInputs();
          setStep("success");
        },
        onError: (error) => {
          const action = resolveAuthError(error);

          // OTP_INVALID -> the code entered earlier was wrong; send the user
          // back to the verification step to re-enter it.
          if (action.code === "OTP_INVALID") {
            setOtpError(action.message);
            otpForm.reset({ otp: "" });
            resetForm.reset({ password: "", confirmPassword: "" });
            clearPasswordInputs();
            setStep("verify");
            return;
          }

          // VALIDATION_ERROR -> map password field messages inline.
          const fieldKeys = Object.keys(action.fieldErrors);
          let mappedAny = false;
          fieldKeys.forEach((key) => {
            if (key === "password" || key === "confirmPassword") {
              resetForm.setError(key, { message: action.fieldErrors[key] });
              mappedAny = true;
            }
          });

          if (!mappedAny) setResetError(action.message);
        },
      },
    );
  };

  const handleBack = () => {
    if (step === "verify") {
      setStep("request");
      return;
    }

    if (step === "reset") {
      resetForm.reset({ password: "", confirmPassword: "" });
      clearPasswordInputs();
      setStep("verify");
      return;
    }

    clearPasswordInputs();
    navigate("/login");
  };

  return (
    <div className="flex flex-col gap-6 ">
      {step !== "success" && (
        <button
          type="button"
          onClick={handleBack}
          className="self-start flex items-center gap-1.5 text-body-sm font-medium text-surface-on-variant dark:text-[#C3C9B4] hover:text-surface-on dark:hover:text-white hover:underline transition-all"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("common.back")}
        </button>
      )}

      {step === "request" && (
        <div className="flex flex-col gap-6 animate-fade-in">
          <div className="text-center">
            <h1 className="text-h3 font-bold text-surface-on dark:text-white">
              {t("auth.forgotPassword")}
            </h1>
            <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
              {t("auth.forgotPasswordSubtitle", {
                defaultValue: "Enter your email to request a password reset code",
              })}
            </p>
          </div>

          <form
            noValidate
            onSubmit={requestForm.handleSubmit(onRequestSubmit)}
            className="flex flex-col gap-4"
          >
            {requestError && (
              <Alert variant="destructive" className="ps-10">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{requestError}</AlertDescription>
                <button
                  type="button"
                  onClick={() => setRequestError(null)}
                  className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </Alert>
            )}
            <Input
              id="email"
              type="email"
              autoComplete="email"
              label={t("auth.email")}
              placeholder={t("auth.emailPlaceholder")}
              error={requestForm.formState.errors.email?.message}
              required
              {...requestForm.register("email")}
            />

            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={forgotPassword.isPending}
              className="w-full mt-2"
            >
              {t("auth.sendResetCode")}
            </Button>
          </form>
        </div>
      )}

      {step === "verify" && (
        <div className="flex flex-col gap-6 animate-fade-in">
          <div className="text-center">
            <h1 className="text-h3 font-bold text-surface-on dark:text-white">{t("auth.otp")}</h1>
            <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
              {t("auth.otpSent", { contact })}
            </p>
          </div>

          <form
            onSubmit={otpForm.handleSubmit(onOtpSubmit)}
            className="flex flex-col items-center gap-6"
            style={{ direction: "ltr" }}
          >
            <div className="flex flex-col gap-2 items-center">
              <InputOTP
                maxLength={4}
                value={otpForm.watch("otp") || ""}
                onChange={(val) => otpForm.setValue("otp", val)}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                </InputOTPGroup>
              </InputOTP>
              {(otpForm.formState.errors.otp || otpError) && (
                <p className="flex items-center gap-1.5 text-xs text-error font-label font-bold">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {otpForm.formState.errors.otp?.message ?? otpError}
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={(otpForm.watch("otp") || "").length < 4}
            >
              {t("common.confirm")}
            </Button>
          </form>
        </div>
      )}

      {step === "reset" && (
        <div className="flex flex-col gap-6 animate-fade-in">
          <div className="text-center">
            <h1 className="text-h3 font-bold text-surface-on dark:text-white">
              {t("auth.resetPassword")}
            </h1>
            <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
              {t("auth.createNewPassword", {
                defaultValue: "Create your new secure password",
              })}
            </p>
          </div>

          <form
            noValidate
            onSubmit={resetForm.handleSubmit(onResetSubmit)}
            autoComplete="off"
            className="flex flex-col gap-4"
          >
            {resetError && (
              <Alert variant="destructive" className="ps-10">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{resetError}</AlertDescription>
                <button
                  type="button"
                  onClick={() => setResetError(null)}
                  className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </Alert>
            )}
            <PasswordInput
              id="password"
              autoComplete="new-password"
              label={t("auth.password")}
              placeholder={t("auth.passwordPlaceholder")}
              error={
                resetForm.formState.errors.password?.message
                  ? t(resetForm.formState.errors.password.message)
                  : undefined
              }
              showStrengthRequirements
              requirementLabels={passwordRequirementLabels}
              required
              {...resetForm.register("password")}
            />

            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              label={t("auth.confirmPassword")}
              placeholder={t("auth.confirmPasswordPlaceholder")}
              error={
                resetForm.formState.errors.confirmPassword?.message
                  ? t(resetForm.formState.errors.confirmPassword.message)
                  : undefined
              }
              required
              {...resetForm.register("confirmPassword")}
            />

            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={resetPassword.isPending}
              className="w-full mt-2"
            >
              {t("auth.resetPassword")}
            </Button>
          </form>
        </div>
      )}

      {step === "success" && (
        <div className="flex flex-col items-center gap-6 py-6 text-center animate-scale-in">
          <div className="h-16 w-16 bg-success/10 rounded-full flex items-center justify-center text-success mb-2">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <div>
            <h1 className="text-h3 font-bold text-surface-on dark:text-white">
              {t("auth.resetPasswordSuccess")}
            </h1>
            <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-2">
              {t("auth.signInWithNewPassword", {
                defaultValue: "You can now sign in using your new password.",
              })}
            </p>
          </div>

          <Button
            onClick={() => {
              clearPasswordInputs();
              navigate("/login");
            }}
            variant="default"
            size="lg"
            className="w-full mt-2"
          >
            {t("auth.login")}
          </Button>
        </div>
      )}
    </div>
  );
}
